import AppContext from 'App/AppContext';
import { IPDropdown, IPLink, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectableOption } from 'baseComponents/Utils/SelectableOption';
import TemplateCriteriaImportPanel from 'components/AuditCriteria/TemplateCriteriaImportPanel';
import GenericModal from 'components/Dialogs/GenericModal';
import { getErrorMessageText } from 'components/Notification/ErrorMessageService';
import { getEntityTypeName } from 'globalFunctions';
import { globalStackTokensGapMedium } from 'globalStyles';
import {
  genericAuditCriteriaFamilies,
  getGenericAuditCriteriaHash,
  getGenericAuditCriteriaSetName,
  getGenericAuditCriteriaSubTypes,
  getGenericAuditCriteriaSubTypeText,
  GenericAuditCriteriaSetRow,
} from 'models/auditCriteriaGenericSets';
import { EntityTypes } from 'models/entity';
import Task, { TaskTypes } from 'models/tasks/task';
import { TFunction } from 'i18next';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  apiCreateGenericAuditCriteriaSet,
  apiDeleteGenericAuditCriteriaSet,
  apiGetGenericAuditCriteriaSets,
} from 'services/Api/auditCriteriaService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';

interface ITemplateCriteriaUpgradeWizardProps {
  isOpen: boolean;
  // what to upgrade: a task or event template, or a recurring annual plan task (a series master, PLAN-CTI-085 A1); it
  // has a checklist or a form
  template: Task;
  onClose: () => void;
}

// the subtype dropdown's key for the family set ("Every <family>")
const familySetKey = 'all';

// where a generic set is edited: Administration > Company > Modules, the family's card
const modulesRoute = '/admin/settings/modules';

/**
 * "Upgrade to audit criteria" from the templates overview (PLAN-CTI-085, spec 7.2). Step 1 picks the generic set the
 * template's checklist and form go to: a family and, for an asset or a processing activity, a subtype. "Next" gets or
 * creates that set (the create is idempotent). Step 2 is the generic editor's "Add from template" dialog with this
 * template preselected and locked. A set the wizard created is removed again when it is closed without an add; after an
 * add it ends on where the criteria went, with a link to the set.
 */
const TemplateCriteriaUpgradeWizard = (props: ITemplateCriteriaUpgradeWizardProps) => {
  // asset and processingActivity: the subtype names (asset types, processing roles) in the dropdown and the set name
  const { t } = useTranslation(['criteria', 'translation', 'exceptions', 'asset', 'processingActivity']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const [step, setStep] = useState<1 | 2 | 'done'>(1);
  const [family, setFamily] = useState<EntityTypes>(genericAuditCriteriaFamilies[0].entityType);
  const [subType, setSubType] = useState<number | undefined>(undefined);
  // the family's existing sets, for the "(new)" mark; undefined while loading
  const [familyRows, setFamilyRows] = useState<GenericAuditCriteriaSetRow[] | undefined>(undefined);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [setId, setSetId] = useState<number>(0);
  const [isCreated, setIsCreated] = useState<boolean>(false);
  const [isAdded, setIsAdded] = useState<boolean>(false);

  const getErrorText = (err: unknown): string =>
    err instanceof AppError ? getErrorMessageText(err, t) : t('exceptions:General.UnknownError');

  useEffect(() => {
    if (!props.isOpen) return;

    setStep(1);
    setFamily(genericAuditCriteriaFamilies[0].entityType);
    setSubType(undefined);
    setSetId(0);
    setIsCreated(false);
    setIsAdded(false);
  }, [props.isOpen]);

  useEffect(() => {
    if (!props.isOpen) return;

    let cancelled = false;
    setFamilyRows(undefined);
    const load = async () => {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const rows = await apiGetGenericAuditCriteriaSets(family, accessToken);
        if (!cancelled) setFamilyRows(rows);
      } catch (err) {
        if (!cancelled) {
          setFamilyRows([]);
          appContext.showNotification(getErrorText(err), true, false, true);
        }
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen, family]);

  const setName = getGenericAuditCriteriaSetName(family, subType, t as TFunction<string[]>);
  const isSeries = props.template.taskType === TaskTypes.Monitoring;
  const exists = (familyRows ?? []).some((row) => row.subType === subType);

  const next = async () => {
    try {
      setIsBusy(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const set = await apiCreateGenericAuditCriteriaSet(family, subType, false, accessToken);
      if (!set.auditCriteriaSetId) return;

      setSetId(set.auditCriteriaSetId);
      setIsCreated(!exists);
      if (!exists)
        appContext.showNotification(t('criteria:TemplateImport.Wizard.Created', { set: setName }), false, false, true);
      setStep(2);
    } catch (err) {
      appContext.showNotification(getErrorText(err), true, false, true);
    } finally {
      setIsBusy(false);
    }
  };

  // the import dialog closed: after an add the wizard says where the criteria went; without one a set it made goes
  const closeImport = async () => {
    if (isAdded) {
      setStep('done');

      return;
    }
    if (isCreated && setId) {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        await apiDeleteGenericAuditCriteriaSet(setId, false, accessToken);
        appContext.showNotification(t('criteria:TemplateImport.Wizard.Removed', { set: setName }), false, false, true);
      } catch (err) {
        appContext.showNotification(getErrorText(err), true, false, true);
      }
    }
    props.onClose();
  };

  const openSet = () => {
    history.push(`${modulesRoute}${getGenericAuditCriteriaHash(family)}`);
    props.onClose();
  };

  const familyOptions: IPSelectableOption[] = genericAuditCriteriaFamilies.map((item) => ({
    key: item.entityType,
    text: getEntityTypeName(item.entityType, t as TFunction<string[]>),
  }));

  const subTypes = getGenericAuditCriteriaSubTypes(family);
  const subTypeOptions: IPSelectableOption[] = [
    {
      key: familySetKey,
      text: t('criteria:TemplateImport.Wizard.SubTypeAll', {
        family: getEntityTypeName(family, t as TFunction<string[]>),
      }),
    },
    ...subTypes.map((item) => ({
      key: item,
      text: getGenericAuditCriteriaSubTypeText(family, item, t as TFunction<string[]>),
    })),
  ];

  if (step === 2) {
    return (
      <TemplateCriteriaImportPanel
        isOpen={props.isOpen}
        auditCriteriaSetId={setId}
        targetName={setName}
        // a template is chosen from the templates list; a recurring annual plan task is not in it and comes as the source
        initialTaskId={isSeries ? undefined : props.template.taskId}
        source={isSeries ? props.template : undefined}
        onClose={() => void closeImport()}
        onImported={() => setIsAdded(true)}
      />
    );
  }

  if (step === 'done') {
    return (
      <GenericModal
        isOpen={props.isOpen}
        onClose={props.onClose}
        title={t('criteria:TemplateImport.Wizard.Title')}
        subTitle={props.template.name}
        closeButtonText={t('criteria:Close')}
      >
        <IPStack tokens={globalStackTokensGapMedium}>
          <IPText>{t('criteria:TemplateImport.Wizard.Done', { set: setName })}</IPText>
          <IPLink onClick={openSet}>{t('criteria:TemplateImport.Wizard.OpenSet')}</IPLink>
        </IPStack>
      </GenericModal>
    );
  }

  return (
    <GenericModal
      isOpen={props.isOpen}
      isBlocking
      onClose={props.onClose}
      title={`${t('criteria:TemplateImport.Wizard.Title')}, ${t('criteria:TemplateImport.Wizard.Step', { n: 1 })}`}
      subTitle={props.template.name}
      onSave={next}
      keepOpenAfterSave={true}
      isActionPending={isBusy}
      saveButtonText={t('criteria:TemplateImport.Wizard.Next')}
      saveDisabled={isBusy || familyRows === undefined}
      closeButtonText={t('translation:General.Button.Cancel')}
      width={520}
      maxWidth={'min(90vw, 520px)'}
    >
      <IPStack tokens={globalStackTokensGapMedium}>
        <IPDropdown
          label={t('criteria:TemplateImport.Wizard.Family')}
          options={familyOptions}
          selectedKey={family}
          disabled={isBusy}
          onChange={(_, option) => {
            if (option === undefined) return;
            setFamily(option.key as EntityTypes);
            setSubType(undefined);
          }}
        />
        {/* the subtype row and the target line keep their place for every family and while the sets load, so the dialog
            keeps its height when the target changes */}
        <IPStack.Item styles={{ root: { visibility: subTypes.length > 0 ? 'visible' : 'hidden' } }}>
          <IPDropdown
            label={t('criteria:TemplateImport.Wizard.SubType')}
            options={subTypeOptions}
            selectedKey={subType ?? familySetKey}
            disabled={isBusy || subTypes.length === 0}
            onChange={(_, option) =>
              setSubType(option === undefined || option.key === familySetKey ? undefined : Number(option.key))
            }
          />
        </IPStack.Item>
        <IPStack.Item styles={{ root: { minHeight: 20 } }}>
          {familyRows !== undefined && (
            <IPText>
              {exists
                ? t('criteria:TemplateImport.Wizard.TargetLine', { set: setName })
                : t('criteria:TemplateImport.Wizard.TargetLineNew', { set: setName })}
            </IPText>
          )}
        </IPStack.Item>
      </IPStack>
    </GenericModal>
  );
};

export default TemplateCriteriaUpgradeWizard;
